// @flow
import React from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { Link, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import './goals.css';
import './cards.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { sendAnalyticsEvent } from 'utils/analyticsWrapper';

import Avatar from './Avatar';

const Header = ({
  goal,
  color,
  showLink,
  owner,
}) => (
  <div className={`dashboard-card-heading pointer ${color}`}>
    <div className="card-heading-text" style={{ textAlign: 'center' }}>
      <div>{goal.name}</div>
    </div>
  </div>
);

const HeaderClickeable = ({
  goal,
  color,
  showLink,
  owner,
}) => {
  const location = useLocation();

  const dispatch = useDispatch();

  const onNavigate = () => {
    sendAnalyticsEvent('Public Profile', 'public_profile_select_goal');

    dispatch({ type: 'GET_PUBLIC_GOAL', data: goal });
  };

  return (

    <div className={`dashboard-card-heading with-arrow pointer ${color}`}>
      <Link onClick={onNavigate} className="goalDetailsBtn" style={{ color: 'white' }} to={`${location.pathname}/${goal.alias}`}>
        <div className="card-heading-text">
          {goal.name}
        </div>
        <span className="shareGoal">
          <FontAwesomeIcon icon={faChevronRight} style={{ color: 'white', fontSize: '18px' }} />
        </span>
      </Link>
    </div>

  );
};

type DetailsProps = {
  endDate: String,
  supportersCount: integer
}

const trackSelectGoalcard = () => {
  sendAnalyticsEvent('Goal', 'goal_give_goalcard_click');
};

const trackShareGoal = () => {
  sendAnalyticsEvent('Goal Share Popup', 'goal_share_popup_open');
};

// This component shows a label with supporters count and (date or no end date)
const Details = ({ endDate, supportersCount }: DetailsProps) => {
  let content;

  const { t } = useTranslation();

  const dateText = endDate ? `${t('enddate_title')}: ${moment(endDate).format('MM/DD/YYYY')}` : t('noenddate_title');

  if (supportersCount && supportersCount > 0) {
    content = `${supportersCount || 0} ${t('supporter_title', { count: supportersCount })} • ${dateText}`;
  } else {
    content = dateText;
  }

  return (<div className="content-text">{content}</div>);
};

const FullViewContent = (props) => {
  const { data, owner } = props;

  const { t } = useTranslation();

  const location = useLocation();

  data.supportersText = t('supporter_title', { count: data.supportersCount });

  const hasFixedAmount = data.amount && data.amount > 0;

  const showSupportersLine = !hasFixedAmount; // if there is not a fixed amount, show supporters line separator.

  const showContainerDescription = props.description || props.wishURL;

  let amountReached;

  if (hasFixedAmount) {
    amountReached = parseFloat(data.publicAchievedAmount) / parseFloat(data.amount);

    if (amountReached > 1) {
      amountReached = 1;
    }

    amountReached *= 100;
  }

  return (
    <>
      <div className={`dashboard-card-content ${props.color} `}>

        {/* Render goal avatar/custom image */}
        {props.avatar}

        <div className="supporters-container" style={{ paddingLeft: '20px' }}>

          <div id="amountContent">
            <span className="amount-text">
              {`$${parseFloat(data.publicAchievedAmount || 0).toFixed(2)}`}
            </span>
            <br />
            <span className="content-text">{t('saved_toward_goals')}</span>
          </div>

          {showSupportersLine && <hr className="supporters-separator" />}

          {
            hasFixedAmount && (
              <div className="progressCircle">
                <div className={`progress-bar ${props.color}`}>
                  <span style={{ width: `${amountReached}%` }}><span className="progress" /></span>
                </div>
              </div>
            )
          }

          <Details supportersCount={data.investorCount} endDate={data.enddate} />
        </div>
        {
          props.aloneView === false && (
            <div className="center">
              <div className="text-center button-support-container">
                <Link
                  to={{ pathname: '/selectgoalcard', state: { receiverGoal: data, receiverUser: owner } }}
                  onClick={trackSelectGoalcard}
                  className={`dashboard-card-button pointer button ${props.color}`}
                >
                  {t('support_goal_title')}

                </Link>
              </div>
            </div>
          )
        }
      </div>
      {
        props.aloneView && (
          <div className="dashboard-card-buttons">
            <Link to={{ pathname: '/selectgoalcard', state: { receiverGoal: data, receiverUser: owner } }} onClick={trackSelectGoalcard} type="button" id="btn_shareGoal" className="button dashboard-card-button">GIVE TO GOAL</Link>
            <Link to={`${location.pathname}/share`} onClick={trackShareGoal} className="button dashboard-card-button">SHARE GOAL</Link>
            {/* <button type="button" id="btn_shareGoal" className="button dashboard-card-button">SHARE GOAL</button> */}
          </div>
        )
      }
      {
        showContainerDescription && (
          <div id="descriptionContainer" className="description-container">
            <hr />
            <p>
              {props.description}
            </p>
            <p>
              {props.wishURL && (
                <a
                  href={props.wishURL}
                  id="linkToProduct"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-to-product"
                >
                  More Details
                </a>
              )}

            </p>
          </div>
        )
      }
    </>
  );
};

const HideAmountContent = (props) => {
  const { data, owner } = props;

  const { t } = useTranslation();

  const location = useLocation();

  const hasFixedAmount = data.amount || data.amount > 0;

  let amountReached;

  const showContainerDescription = props.description || props.wishURL;

  if (hasFixedAmount) {
    amountReached = parseFloat(data.publicAchievedAmount) / parseFloat(data.amount);

    if (amountReached > 1) {
      amountReached = 1;
    }

    amountReached *= 100;
  }

  return (
    <>
      <div className={`dashboard-card-content ${props.color}`}>

        {/* Render goal avatar/custom image */}
        {props.avatar}

        <div className="supporters-container" style={{ paddingLeft: '20px' }}>
          {
            hasFixedAmount === false
            && (
              <>
                <div>
                  {
                    data.investorCount > 0 ? `${data.investorCount} ${t('supporter_title', { count: data.investorCount }).toUpperCase()}` : 'BE THE FIRST SUPPORTER!'
                  }
                </div>
                <hr className="supporters-separator" />
                <Details endDate={data.enddate} />
              </>
            )
          }
          {
            hasFixedAmount && (
              <>
                <div id="amountContent">
                  <span className="amount-text">
                    {`${amountReached.toFixed(2)}%`}
                  </span>
                  <br />
                  <span className="content-text">{t('saved_toward_goals')}</span>
                </div>

                <div className="progressCircle">
                  <div className={`progress-bar ${props.color}`}>
                    <span style={{ width: `${amountReached}%` }}><span className="progress" /></span>
                  </div>
                </div>

                <Details supportersCount={data.investorCount} endDate={data.enddate} />
              </>
            )
          }

        </div>

        {
          props.aloneView === false && (
            <div className="center">
              <div className="text-center button-support-container">
                <Link to={{ pathname: '/selectgoalcard', state: { receiverGoal: data, receiverUser: owner } }} onClick={trackSelectGoalcard} className={`dashboard-card-button pointer button ${props.color}`}>{t('support_goal_title')}</Link>
              </div>
            </div>
          )
        }

      </div>

      {
        props.aloneView && (
          <div className="dashboard-card-buttons">
            <Link to={{ pathname: '/selectgoalcard', state: { receiverGoal: data, receiverUser: owner } }} onClick={trackSelectGoalcard} className="button dashboard-card-button">GIVE TO GOAL</Link>
            <Link to={`${location.pathname}/share`} onClick={() => trackShareGoal} className="button dashboard-card-button">SHARE GOAL</Link>
          </div>
        )
      }
      {
        showContainerDescription && (
          <div id="descriptionContainer" className="description-container">
            <hr />
            <p>
              {props.description}
            </p>
            <p>
              {props.wishURL && (
                <a
                  href={props.wishURL}
                  id="linkToProduct"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-to-product"
                >
                  More Details
                </a>
              )}

            </p>
          </div>
        )
      }
    </>
  );
};

const Goal = (props) => (
  <div className={`dashboard-card goalcard goal-focusable ${props.color} finished`}>

    {props.header}

    {props.content}

  </div>
);

const GoalAlone = (props) => (
  <div className={`dashboard-card goalcard goal-focusable ${props.color} finished ${(props.description || props.wishURL) ? 'with-description' : 'no-description'}`}>

    {props.header}

    {props.content}

  </div>
);

type CardGoalProps = {
  data: any,
  owner: any,
  color: string,
  aloneView: boolean
}

const getGoalColorByTypeAndIndex = (type, index) => {
  let color = 'lightblue';

  switch (type) {
    case 1:
      color = 'purple';
      break;
    case 2:
      color = 'blue';
      break;
    case 3:
      color = 'darkblue';
      break;
    default:
      if (index && (index % 2) === 0) {
        color = 'lightblue';
      } else {
        color = 'steelblue';
      }
      break;
  }

  return color;
};

const CardGoal = (props: CardGoalProps) => {
  const {
    data, aloneView, owner, idx,
  } = props;

  const color = getGoalColorByTypeAndIndex(data.type, idx);

  const showLink = !aloneView;

  const header = showLink
    ? <HeaderClickeable goal={props.data} color={color} owner={owner} />
    : <Header goal={props.data} color={color} owner={owner} />;

  const avatarGoal = (
    <Avatar aloneView={aloneView} goal={data} color={color} />
  );

  const cardContentInner = data.visibilityType === 0 ? <FullViewContent color={color} aloneView={aloneView} data={data} avatar={avatarGoal} owner={owner} />
    : <HideAmountContent color={color} aloneView={aloneView} data={data} avatar={avatarGoal} owner={owner} />;

  const result = aloneView ? <GoalAlone color={color} header={header} content={cardContentInner} owner={owner} />
    : <Goal color={color} header={header} content={cardContentInner} owner={owner} />;

  return result;
};

export default CardGoal;
